// "Continue a link after sign-in": a signed-out visit to a deep link remembers where it was going,
// and the sign-in success sites send the person there instead of /map. sessionStorage, so it dies with the tab.
const KEY = "ieup.returnTo";

const AUTH_ROUTES = ["/login", "/auth", "/first-login", "/forgot-password"];

/** The path when it is a safe in-app, non-auth route (search + hash kept); otherwise null. */
export function safeReturnPath(p: string | null | undefined): string | null {
  if (!p || !p.startsWith("/")) return null;
  // Control characters and backslashes are stripped / rewritten by browsers (`/\t/evil.com` becomes `//evil.com`).
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F\\]/.test(p)) return null;
  if (p.startsWith("//")) return null;
  try {
    if (new URL(p, window.location.origin).origin !== window.location.origin) return null;
  } catch {
    return null;
  }
  const route = p.split(/[?#]/)[0];
  if (AUTH_ROUTES.some((a) => route === a || route.startsWith(`${a}/`))) return null;
  return p;
}

export function rememberReturnTo(path: string): void {
  const safe = safeReturnPath(path);
  if (!safe) return;
  try {
    sessionStorage.setItem(KEY, safe);
  } catch {
    /* storage blocked: the link is simply not continued */
  }
}

// One-shot marker set by an explicit sign-out: the guard's redirect that follows must not remember the page the
// person signed out from. Consumed by that redirect (or by the next sign-in, if no guard was mounted).
let signedOut = false;

/** An explicit sign-out: drop any waiting link and keep the guard from remembering the page being left. */
export function markSignedOut(): void {
  clearReturnTo();
  signedOut = true;
}

/** Remembers the link a signed-out redirect leaves, unless this redirect is an explicit sign-out (then the marker is spent). */
export function rememberReturnToUnlessSignedOut(path: string): void {
  if (signedOut) {
    signedOut = false;
    return;
  }
  rememberReturnTo(path);
}

export function takeReturnTo(): string | null {
  try {
    const stored = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return safeReturnPath(stored);
  } catch {
    return null;
  }
}

export function clearReturnTo(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}

/** Where a successful sign-in lands; an incomplete profile goes to /first-login with the link kept for later. */
export function homeAfterSignIn(hasInformation: boolean): string {
  if (!hasInformation) return "/first-login";
  signedOut = false;
  return takeReturnTo() ?? "/map";
}
